import BaseUI from "../base-ui.ts";
import LogoFragment from "../fragments/logo.ts";
import State from "../../state/state.ts";
import EatOutApiClient from "../../api-client/eat-out-api-client.ts";
import UiUtils from "../../utils/ui-utils.ts";
import Route from "../../enums/route.ts";
import StateUtils from "../../state/state-utils.ts";
import Group from "../../models/group.ts";
import LogUtils from "../../utils/log-utils.ts";
import Restaurant from "../../models/restaurant.ts";

export default class FinalResultScreen extends BaseUI {
  constructor(
    state: State,
    apiClient: EatOutApiClient,
    navigateCallback: (route: Route) => void,
  ) {
    super("final-result-screen", state, apiClient, navigateCallback);
  }

  public override render(newElements: HTMLElement[] = []): HTMLDivElement {
    // logo
    const logo: LogoFragment = new LogoFragment(this.state, this.apiClient, this.navigateCallback);
    newElements.push(logo.render());

    // get the best restaurant ID
    const group: Group = StateUtils.getCurrentGroup(this.state);
    const bestRestaurantId: string | null = group.bestRestaurant;
    if (bestRestaurantId === null) {
      LogUtils.error("attempted to render FinalResults screen, but the current group doesn't have a 'bestRestaurant'");
    }

    // find the best restaurant
    let bestRestaurant: Restaurant | null = null;
    const topRestaurants: Restaurant[] = StateUtils.getCurrentGroupTopRestaurants(this.state);
    for (const topRestaurant of topRestaurants) {
      // found the best restaurant by ID
      if (topRestaurant.id === bestRestaurantId) {
        bestRestaurant = topRestaurant;
        break;
      }
    }
    if (bestRestaurant === null) {
      LogUtils.error(
        "attempted to render FinalResults screen, but couldn't find the Best Restaurant from the 'topRestaurants' data",
      );
    }

    // display best restaurant
    newElements.push(this.generateFinalRestaurantDiv(bestRestaurant));

    // done button
    const doneButton: HTMLButtonElement = UiUtils.button("Done", () => this.navigateCallback(Route.MAIN_MENU));
    newElements.push(doneButton);

    return super.render(newElements);
  }

  private generateFinalRestaurantDiv(bestRestaurant: Restaurant): HTMLDivElement {
    const finalRestaurantDiv: HTMLDivElement = UiUtils.div("final-restaurant");

    // name
    const name: HTMLHeadingElement = UiUtils.heading(4, bestRestaurant.name);
    name.id = "final-restaurant-name";
    finalRestaurantDiv.appendChild(name);

    // image
    const image: HTMLImageElement = UiUtils.image(bestRestaurant.imageUrl);
    image.id = "final-restaurant-image";
    finalRestaurantDiv.appendChild(image);

    // rating
    const rating: HTMLParagraphElement = UiUtils.paragraph(`Rating: ${bestRestaurant.rating}`);
    rating.id = "final-restaurant-rating";
    finalRestaurantDiv.appendChild(rating);

    // reviews
    const reviews: HTMLParagraphElement = UiUtils.paragraph(`Reviews: ${bestRestaurant.reviewCount}`);
    reviews.id = "final-restaurant-reviews";
    finalRestaurantDiv.appendChild(reviews);

    // price
    const price: HTMLParagraphElement = UiUtils.paragraph(`Price: ${bestRestaurant.price}`);
    price.id = "final-restaurant-price";
    finalRestaurantDiv.appendChild(price);

    // address
    const addressContainer: HTMLElement = UiUtils.address("final-restaurant-address");
    addressContainer.appendChild(UiUtils.paragraph(bestRestaurant.location.address1));
    if (bestRestaurant.location.address2 !== null) {
      addressContainer.appendChild(UiUtils.paragraph(bestRestaurant.location.address2));
    }
    if (bestRestaurant.location.address3 !== null) {
      addressContainer.appendChild(UiUtils.paragraph(bestRestaurant.location.address3));
    }
    addressContainer.appendChild(
      UiUtils.paragraph(`${bestRestaurant.location.city}, ${bestRestaurant.location.state}`),
    );
    addressContainer.appendChild(
      UiUtils.paragraph(bestRestaurant.location.zipCode),
    );
    finalRestaurantDiv.appendChild(addressContainer);

    // phone
    const phone: HTMLAnchorElement = UiUtils.clickablePhoneNumber(bestRestaurant.phone, bestRestaurant.displayPhone);
    phone.id = "final-restaurant-phone";
    addressContainer.appendChild(phone);

    // website
    const website: HTMLAnchorElement = UiUtils.anchor("Website", bestRestaurant.url);
    website.id = "final-restaurant-website";
    addressContainer.appendChild(website);

    return finalRestaurantDiv;
  }
}
